/**
 * Valeur déclarée d'un envoi, telle que le client l'a saisie — lecture seule.
 *
 * ## La seule forme écrite
 *
 * `parcelInfo.value = { amount, currency: 'EUR', declared: true, insured: false }`,
 * posée par le constructeur de brouillon du domaine
 * (`packages/domain/shipment/src/shipment/draft/shipment-draft-builder.ts`)
 * uniquement quand une valeur est présente ; l'adaptateur du tunnel la calcule
 * comme la SOMME des valeurs d'articles retenus × quantité. Le contrat
 * (`unified-shipment-draft.schema.ts`) exige `amount` fini et ≥ 0, et une devise.
 *
 * Homonymes qui ne sont PAS cette donnée, et que cette lecture ignore donc :
 * `incomingPackage.declaredValue` (colis scanné au Virtual Mailbox),
 * `parcelInfo.parcels[].declaredValue` (commande B2B du portail),
 * `parcelInfo.items[].value` (valeur unitaire d'un article).
 *
 * ## Ce que la fonction garantit
 *
 * Elle rend `null` dès que la valeur est absente ou inexploitable (montant non
 * numérique, non fini, négatif ; devise absente). L'écran affiche alors
 * « non déclarée » : un montant n'est JAMAIS inventé — en particulier pas un
 * `0 €` qui se lirait comme une déclaration réelle lors d'une réclamation.
 */
export interface DeclaredValue {
  amount: number;
  currency: string;
}

export function readDeclaredValue(shipment: unknown): DeclaredValue | null {
  const value = (shipment as any)?.parcelInfo?.value;
  if (value == null || typeof value !== 'object') return null;

  const amount = value.amount;
  if (typeof amount !== 'number' || !Number.isFinite(amount) || amount < 0) return null;

  const currency = typeof value.currency === 'string' ? value.currency.trim().toUpperCase() : '';
  if (!currency) return null;

  return { amount, currency };
}
